import { siteConfig } from '@afrilink/config';

/**
 * Client-side JS error reporter (M3.13). Wires window.onerror +
 * window.onunhandledrejection → marketing-error-log Edge Function. Errors
 * land in public.marketing_error for staff review.
 *
 * Designed to be cheap and resilient:
 *  - Fire-and-forget POST (errors in the reporter itself never bubble)
 *  - keepalive: true so reports survive page unloads
 *  - Coarse rate limit at the EF (30/min/IP) handles runaway clients
 *  - First-party (no third-party tracker, no consent gate needed)
 *
 * Optional bot heuristic: skips obvious crawlers (Googlebot etc.) so the
 * table stays focused on real-user errors.
 */

interface ErrorPayload {
  message: string;
  stack?: string;
  source?: string;
  line_no?: number;
  col_no?: number;
}

const BOT_UA_RE = /\b(bot|spider|crawl|crawler|slurp|preview|prerender|fetch|monitor|headless)\b/i;

let installed = false;

export function installErrorReporter(): void {
  if (installed) return;
  installed = true;

  if (typeof window === 'undefined') return;
  if (BOT_UA_RE.test(navigator.userAgent || '')) return;

  window.addEventListener('error', (event) => {
    void report({
      message: event.message || 'window.onerror (no message)',
      stack: event.error instanceof Error ? event.error.stack : undefined,
      source: event.filename || undefined,
      line_no: event.lineno || undefined,
      col_no: event.colno || undefined,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    void report({
      message:
        reason instanceof Error
          ? `Unhandled rejection: ${reason.message}`
          : `Unhandled rejection: ${String(reason)}`,
      stack: reason instanceof Error ? reason.stack : undefined,
    });
  });
}

async function report(payload: ErrorPayload): Promise<void> {
  try {
    await fetch(siteConfig.marketingErrorLogEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...payload,
        url: typeof window !== 'undefined' ? window.location.href : undefined,
        user_agent:
          typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      }),
      keepalive: true,
    });
  } catch {
    // Reporter must never throw. If we can't reach the EF, swallow.
  }
}
